import React from 'react';
import { InvoiceStatus } from '@types';
import { useStatuses } from '@hooks/useStatuses';
import { FALLBACK_STATUSES } from '@hooks/useStatusPreloader';
import { SimpleTooltip, TooltipProvider } from '@components/ui';
import { useAuth } from '@contexts/AuthContext';

// Pure helper to filter statuses based on role flags (stable reference)
const filterStatusesByRoleHelper = (
  statuses: Array<{ key: string; label: string; color?: string }>,
  hasStock: boolean,
  hasAdminOrFinance: boolean
) => {
  return statuses.filter(status => {
    if (hasStock && !hasAdminOrFinance) {
      // Stock users can only see statuses: 2, 3, 4, 5, 8, and 12
      const stockAllowedStatuses = ['adding', 'new_products', 'ir', 'invoicing', 'entering_stock', 'completed'];
      return stockAllowedStatuses.includes(status.key);
    }

    // 'entering_expense' status is visible to admin and finance users
    if (status.key === 'entering_expense') {
      return hasAdminOrFinance;
    }

    // All other statuses are visible to admin and finance users
    return true;
  });
};

export interface ChevronStatusFilterProps {
  selectedStatus: InvoiceStatus | 'all';
  onStatusChange: (status: InvoiceStatus | 'all') => void;
  showAll?: boolean;
  title?: string;
  className?: string;
  filteredCounts?: Record<string, number>; // Override counts with filtered results
  extraFooterRight?: React.ReactNode; // Optional right-side control next to "Currently showing:"
  extraHeaderRight?: React.ReactNode; // Optional right-side control opposite the title
}

export const ChevronStatusFilter: React.FC<ChevronStatusFilterProps> = ({
  selectedStatus,
  onStatusChange,
  showAll = true,
  title = "Invoice Status Overview",
  className = "",
  filteredCounts,
  extraFooterRight,
  extraHeaderRight,
}) => {
  const { user } = useAuth();
  const { 
    getChevronDataWithCounts, 
    chevronData: rawChevronData,
    isLoading: isStatusesLoading,
    invoiceCounts
  } = useStatuses();
  
  // Compute role flags once per user role change
  const roleFlags = React.useMemo(() => ({
    hasStock: user?.roles?.some(role => role.name === 'stock') ?? false,
    hasAdminOrFinance: user?.roles?.some(role => role.name === 'admin' || role.name === 'finance') ?? false,
  }), [user?.roles]);
  
  // Show statuses immediately, use fallback if needed
  const chevronData = React.useMemo(() => {
    // Use filtered counts when provided, otherwise fall back to global counts
    const countsToUse = filteredCounts || invoiceCounts;
    
    let rawData;
    if (isStatusesLoading || !rawChevronData.length) {
      // Use fallback data to show statuses immediately
      rawData = FALLBACK_STATUSES.map(status => ({
        ...status,
        count: countsToUse[status.key] || 0, // Apply counts from countsToUse
      }));
    } else {
      rawData = getChevronDataWithCounts();
    }
    
    // Apply role-based filtering
    let filtered = filterStatusesByRoleHelper(rawData, roleFlags.hasStock, roleFlags.hasAdminOrFinance);
    
    // FALLBACK: If no statuses available, use fallback data
    if (filtered.length === 0) {
      if (roleFlags.hasStock && !roleFlags.hasAdminOrFinance) {
        // Filter fallback statuses for stock users
        const stockAllowedStatuses = ['adding', 'new_products', 'ir', 'invoicing', 'entering_stock', 'completed'];
        filtered = FALLBACK_STATUSES
          .filter(status => stockAllowedStatuses.includes(status.key))
          .map(status => ({
            ...status,
            count: countsToUse[status.key] || 0, // Apply counts from countsToUse
          }));
      } else {
        // For admin/finance users, show all except entering_expense (which is admin/finance-only)
        const isAdmin = roleFlags.hasAdminOrFinance; // either admin or finance
        const isFinance = roleFlags.hasAdminOrFinance;
        filtered = FALLBACK_STATUSES
          .filter(status => {
            // 'entering_expense' status is visible to admin and finance users
            if (status.key === 'entering_expense') {
              return isAdmin || isFinance;
            }
            return true;
          })
          .map(status => ({
            ...status,
            count: countsToUse[status.key] || 0, // Apply counts from countsToUse
          }));
      }
    }
    
    // Ensure counts are properly applied to filtered statuses
    // This fixes the case where backend chevronData doesn't match filtered statuses
    const finalFiltered = filtered.map(status => ({
      ...status,
      count: countsToUse[status.key] || 0,
    }));
    
    return finalFiltered;
  }, [getChevronDataWithCounts, rawChevronData, isStatusesLoading, roleFlags, invoiceCounts, filteredCounts]);
  


  return (
    <TooltipProvider>
      <div className={`bg-white rounded-lg shadow p-6 ${className}`}>
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-secondary-800">{title}</h3>
          <div className="flex items-center space-x-3">
            {extraHeaderRight}
            {showAll && (
              <button
                onClick={() => onStatusChange('all')}
                className={`text-sm px-3 py-1 rounded-full transition-colors ${
                  selectedStatus === 'all'
                    ? 'bg-primary-500 text-white'
                    : 'text-secondary-600 hover:bg-secondary-100'
                }`}
              >
                Show All
              </button>
            )}
          </div>
        </div>
        
        {/* Connected Status Toggle - Fixed Chevron Design */}
        <div 
          className="flex w-full"
          style={{
            borderRadius: '8px'
          }}
        >
        {chevronData.map((status, index) => {
          const isActive = selectedStatus === status.key;
          const hasItems = (status.count || 0) > 0;
          const isFirst = index === 0;
          const isLast = index === chevronData.length - 1;
          
          
          return (
            <div key={status.key} className="relative flex-1">
              <SimpleTooltip 
                text={status.label}
                side="top"
              >
                <button
                  onClick={() => onStatusChange(status.key as InvoiceStatus)}
                  className="flex items-center justify-center px-4 py-2 relative transition-all duration-200 outline-none focus:outline-none w-full"
                style={{
                  backgroundColor: isActive ? '#006633' : 'white',
                  borderColor: isActive ? '#006633' : '#d1d5db',
                  color: isActive ? 'white' : '#374151',
                  paddingLeft: isFirst ? '16px' : '28px',
                  paddingRight: isLast ? '16px' : '16px',
                  zIndex: chevronData.length - index,
                  outline: 'none',
                  boxShadow: 'none',
                  marginRight: isLast ? '0' : '-1px',
                  borderRadius: isFirst && isLast ? '8px' : isFirst ? '8px 0 0 8px' : isLast ? '0 8px 8px 0' : '0',
                  minHeight: '60px',
                  border: isActive ? '2px solid #006633' : '1px solid #d1d5db',
                }}
              >
                {/* Show NUMBER only */}
                <div className="flex items-center justify-center">
                  <span className="text-2xl font-bold">
                    {status.number}
                  </span>
                </div>
                
                {/* Floating Count Badge */}
                {(status.count || 0) > 0 && (
                  <div className="absolute -top-2 right-0 z-20">
                    <span 
                      className="inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none rounded-full min-w-[20px] h-5"
                      style={{
                        backgroundColor: isActive ? 'white' : '#006633',
                        color: isActive ? '#006633' : 'white',
                        boxShadow: isActive ? '0 2px 4px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)' : 'none',
                      }}
                    >
                      {status.count}
                    </span>
                  </div>
                )}
                
                {/* Arrow pointing right (only for non-last segments) */}
                {!isLast && (
                  <div 
                    className="absolute top-0 h-full flex items-center"
                    style={{
                      right: '0', // Position at the button's right edge
                      zIndex: 10,
                      width: '0',
                    }}
                  >
                    <div 
                      className="w-0 h-0"
                      style={{
                        borderTop: '30px solid transparent',
                        borderBottom: '30px solid transparent',
                        borderLeft: `12px solid ${
                          isActive ? '#006633' : '#ffffff'
                        }`,
                        borderRight: 'none',
                        outline: 'none',
                      }}
                    >
                    </div>
                  </div>
                )}
              </button>
              </SimpleTooltip>
              
              {/* Screen reader accessibility */}
              <div className="sr-only">
                Status {status.number}: {status.label} - {status.description}
                {hasItems && ` (${status.count} invoices)`}
              </div>
            </div>
          );
        })}
      </div>
      
      {/* Status Legend with Total Count */}
      <div className="mt-4 text-sm text-gray-600">
        <div className="flex justify-between items-center">
          <span>Click on any number to filter by status</span>
          <span className="font-medium">
            Total: {chevronData.reduce((sum, status) => sum + (status.count || 0), 0)} invoices
          </span>
        </div>
        
        {/* Current selection info */}
        {selectedStatus !== 'all' && (
          <div className="mt-2 flex items-center justify-between">
            <div className="text-xs text-primary-600">
              Currently showing: {chevronData.find(s => s.key === selectedStatus)?.label || selectedStatus}
            </div>
            {extraFooterRight ? (
              <div className="ml-4">
                {extraFooterRight}
              </div>
            ) : null}
          </div>
        )}
        
      </div>
      </div>
    </TooltipProvider>
  );
};
